import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";

export default function Register() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form.name, form.email, form.password);
      toast.success(t("auth.welcomeOlivara"));
      navigate("/");
    } catch (err) {
      toast.error(apiError(err, t, "errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16" data-testid="register-page">
      <div className="max-w-md w-full">
        <Link to="/" className="font-serif text-4xl block">
          <span className="text-olive">OliveOil</span><span className="text-ink-muted/70 font-normal">.shop</span>
        </Link>
        <h1 className="font-serif text-4xl mt-10 tracking-tight">{t("auth.registerTitle")}</h1>
        <p className="text-ink-soft mt-2">{t("auth.alreadyMember")}{" "}
          <Link to="/login" className="underline text-olive" data-testid="link-login">{t("auth.signInTitle")}</Link>
        </p>

        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="register-form">
          <div>
            <label className="overline">{t("auth.name")}</label>
            <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-2 w-full bg-white hairline rounded-sm px-4 py-3 text-sm focus:outline-none focus:border-olive"
              data-testid="register-name" />
          </div>
          <div>
            <label className="overline">{t("auth.email")}</label>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-2 w-full bg-white hairline rounded-sm px-4 py-3 text-sm focus:outline-none focus:border-olive"
              data-testid="register-email" />
          </div>
          <div>
            <label className="overline">{t("auth.password")}</label>
            <input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-2 w-full bg-white hairline rounded-sm px-4 py-3 text-sm focus:outline-none focus:border-olive"
              data-testid="register-password" />
          </div>
          <button type="submit" disabled={busy} className="w-full bg-olive text-bone py-4 rounded-sm hover:bg-olive-dark transition-colors text-sm tracking-wide disabled:opacity-60" data-testid="register-submit">
            {busy ? t("auth.creating") : t("auth.createAccountBtn")}
          </button>
        </form>

        <div className="flex items-center gap-4 my-8">
          <div className="flex-1 h-px bg-edge" /><span className="overline">{t("common.or")}</span><div className="flex-1 h-px bg-edge" />
        </div>

        <button onClick={googleLogin} className="w-full hairline bg-white py-4 rounded-sm hover:bg-boneAlt transition-colors text-sm flex items-center justify-center gap-3" data-testid="google-register-button">
          <svg className="w-4 h-4" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          {t("auth.continueWithGoogle")}
        </button>
      </div>
    </div>
  );
}
